import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import api from "../../services/api";
import { Tenant } from "../../api/types";

interface ApiToken {
  id: number;
  description: string;
  status: string;
  prefix: string;
  last4: string;
  createdBy: number;
  revokedBy?: number;
  rotatedFromId?: number;
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApiTokenCreatedResponse {
  token: string;
  item: ApiToken;
}

export function ApiTokens() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenValue, setTokenValue] = useState("");
  const [modalTitle, setModalTitle] = useState(t("admin.apiTokens.modal.generatedTitle"));

  const logError = (label: string, error: unknown) => {
    if (isAxiosError(error)) {
      console.warn(label, error.response?.status ?? "unknown", error.message);
      return;
    }
    if (error instanceof Error) {
      console.warn(label, error.message);
      return;
    }
    console.warn(label);
  };

  const loadTenants = async () => {
    try {
      const { data } = await api.get<Tenant[]>("/tenants/all");
      setTenants(data || []);
      if (data?.length && !selectedTenantId) {
        setSelectedTenantId(data[0].id);
      }
    } catch (error) {
      logError("Failed to load tenants", error);
      setMessage(t("admin.apiTokens.loadTenantsError"));
    }
  };

  const loadTokens = async (tenantId: number) => {
    setLoading(true);
    setMessage(null);
    try {
      const { data } = await api.get<ApiToken[]>(`/tenants/${tenantId}/api-tokens`);
      setTokens(data || []);
    } catch (error) {
      logError("Failed to load tokens", error);
      setMessage(t("admin.apiTokens.loadTokensError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    if (selectedTenantId) {
      loadTokens(selectedTenantId);
    }
  }, [selectedTenantId]);

  const handleCreate = async () => {
    if (!selectedTenantId) return;
    if (!description.trim()) {
      setMessage(t("admin.apiTokens.descriptionRequired"));
      return;
    }
    let expiresPayload: string | null = null;
    if (expiresAt.trim()) {
      const parsed = new Date(expiresAt);
      if (Number.isNaN(parsed.getTime())) {
        setMessage(t("admin.apiTokens.invalidExpiration"));
        return;
      }
      expiresPayload = parsed.toISOString();
    }
    setActionLoading(true);
    setMessage(null);
    try {
      const { data } = await api.post<ApiTokenCreatedResponse>(`/tenants/${selectedTenantId}/api-tokens`, {
        description: description.trim(),
        expiresAt: expiresPayload,
      });
      setDescription("");
      setExpiresAt("");
      setModalTitle(t("admin.apiTokens.modal.createdTitle"));
      setTokenValue(data.token);
      setShowTokenModal(true);
      await loadTokens(selectedTenantId);
    } catch (error) {
      logError("Failed to create token", error);
      setMessage(t("admin.apiTokens.createError"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async (tokenId: number) => {
    if (!selectedTenantId) return;
    if (!confirm(t("admin.apiTokens.confirmRevoke"))) return;
    setActionLoading(true);
    setMessage(null);
    try {
      await api.post(`/tenants/${selectedTenantId}/api-tokens/${tokenId}/revoke`);
      await loadTokens(selectedTenantId);
    } catch (error) {
      logError("Failed to revoke token", error);
      setMessage(t("admin.apiTokens.revokeError"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRotate = async (tokenId: number) => {
    if (!selectedTenantId) return;
    if (!confirm(t("admin.apiTokens.confirmRotate"))) return;
    setActionLoading(true);
    setMessage(null);
    try {
      const { data } = await api.post<ApiTokenCreatedResponse>(`/tenants/${selectedTenantId}/api-tokens/${tokenId}/rotate`);
      setModalTitle(t("admin.apiTokens.modal.rotatedTitle"));
      setTokenValue(data.token);
      setShowTokenModal(true);
      await loadTokens(selectedTenantId);
    } catch (error) {
      logError("Failed to rotate token", error);
      setMessage(t("admin.apiTokens.rotateError"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (tokenId: number, status: string) => {
    if (!selectedTenantId) return;
    if (status !== "revoked") {
      setMessage(t("admin.apiTokens.deleteOnlyRevoked"));
      return;
    }
    if (!confirm(t("admin.apiTokens.confirmDelete"))) return;
    setActionLoading(true);
    setMessage(null);
    try {
      await api.delete(`/tenants/${selectedTenantId}/api-tokens/${tokenId}`);
      await loadTokens(selectedTenantId);
    } catch (error) {
      logError("Failed to delete token", error);
      setMessage(t("admin.apiTokens.deleteError"));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "—";
    return parsed.toLocaleString();
  };

  const tenantOptions = useMemo(() => tenants || [], [tenants]);
  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === selectedTenantId) ?? null,
    [tenants, selectedTenantId]
  );

  return (
    <div className="shell">
      <div className="header">
        <div className="header-title">
          <h1>{t("admin.apiTokens.title")}</h1>
          <p className="muted">{t("admin.apiTokens.subtitle")}</p>
        </div>
        <button className="secondary" onClick={() => selectedTenantId && loadTokens(selectedTenantId)} disabled={loading}>
          {t("admin.apiTokens.refresh")}
        </button>
      </div>

      {message && (
        <div className="info" style={{ marginBottom: '1.5rem' }}>
          {message}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="stack">
          <label>
            {t("admin.apiTokens.tenant")}
            <select
              className="secondary"
              value={selectedTenantId ?? ""}
              onChange={(e) => setSelectedTenantId(Number(e.target.value))}
              style={{ height: '42px' }}
            >
              {tenantOptions.length === 0 ? (
                <option value="">{t("admin.apiTokens.noTenants")}</option>
              ) : (
                tenantOptions.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} (ID {tenant.id})
                  </option>
                ))
              )}
            </select>
          </label>
          <label>
            {t("admin.apiTokens.descriptionLabel")}
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("admin.apiTokens.descriptionPlaceholder")}
              required
            />
          </label>
          <label>
            {t("admin.apiTokens.expiresLabel")}
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </label>
          <button onClick={handleCreate} disabled={actionLoading || !selectedTenantId}>
            {actionLoading ? t("admin.apiTokens.creating") : t("admin.apiTokens.create")}
          </button>
        </div>
      </div>

      <div className="stack">
        {loading ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <p className="muted">{t("admin.apiTokens.loading")}</p>
          </div>
        ) : tokens.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <p className="muted">{t("admin.apiTokens.empty")}</p>
          </div>
        ) : (
          <div className="grid">
            {tokens.map((item) => (
              <div key={item.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1rem' }}>{item.description}</h3>
                  <p className="muted" style={{ fontSize: '0.75rem' }}>
                    {t("admin.apiTokens.status")}: {item.status} • {t("admin.apiTokens.prefix")}: {item.prefix} •{" "}
                    {t("admin.apiTokens.last4")}: {item.last4}
                  </p>
                  {selectedTenant && (
                    <p className="muted" style={{ fontSize: '0.75rem' }}>
                      {t("admin.apiTokens.tenantLabel")}: {selectedTenant.name}
                    </p>
                  )}
                </div>
                <div style={{ fontSize: '0.8125rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <div>{t("admin.apiTokens.lastUsed")}: {formatDate(item.lastUsedAt)}</div>
                  <div>{t("admin.apiTokens.expiresAt")}: {formatDate(item.expiresAt)}</div>
                  <div>{t("admin.apiTokens.revokedAt")}: {formatDate(item.revokedAt)}</div>
                </div>
                <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'grid', gap: '0.5rem' }}>
                  <button
                    className="secondary small"
                    onClick={() => handleRotate(item.id)}
                    disabled={actionLoading || item.status !== "active"}
                    style={{ fontSize: '0.75rem' }}
                  >
                    {t("admin.apiTokens.rotate")}
                  </button>
                  <button
                    className="secondary small"
                    onClick={() => handleRevoke(item.id)}
                    disabled={actionLoading || item.status !== "active"}
                    style={{ fontSize: '0.75rem', color: '#ef4444', borderColor: '#fecaca' }}
                  >
                    {t("admin.apiTokens.revoke")}
                  </button>
                  <button
                    className="secondary small"
                    onClick={() => handleDelete(item.id, item.status)}
                    disabled={actionLoading || item.status !== "revoked"}
                    style={{ fontSize: '0.75rem', color: '#b91c1c', borderColor: '#fecaca' }}
                  >
                    {t("admin.apiTokens.delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showTokenModal && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '520px', padding: '2rem' }}>
            <h3 style={{ marginBottom: '0.5rem', fontSize: '1.25rem' }}>{modalTitle}</h3>
            <p className="muted" style={{ marginBottom: '1.5rem' }}>{t("admin.apiTokens.modal.description")}</p>
            <div style={{ fontFamily: 'monospace', fontSize: '0.9rem', wordBreak: 'break-all', padding: '0.75rem', background: 'rgba(15,23,42,0.08)', borderRadius: '8px' }}>
              {tokenValue}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button
                className="secondary"
                style={{ flex: 1 }}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(tokenValue);
                    setMessage(t("admin.apiTokens.copied"));
                  } catch {
                    setMessage(t("admin.apiTokens.copyError"));
                  }
                }}
              >
                {t("admin.apiTokens.copy")}
              </button>
              <button
                type="button"
                className="secondary"
                style={{ flex: 1 }}
                onClick={() => setShowTokenModal(false)}
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
